/**
 * Utility functions for JWT authentication
 */

/**
 * Get the current user from localStorage or sessionStorage
 * Checks localStorage first (Remember Me), then sessionStorage
 * @returns {Object|null} User object or null if not logged in
 */
export const getCurrentUser = () => {
  try {
    // Check localStorage first (Remember Me)
    const localUser = localStorage.getItem("user");
    if (localUser) {
      return JSON.parse(localUser);
    }
    
    // Check sessionStorage (session only)
    const sessionUser = sessionStorage.getItem("user");
    if (sessionUser) {
      return JSON.parse(sessionUser);
    }
    
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Get access token from storage
 * @returns {string|null} Access token or null
 */
export const getAccessToken = () => {
  return localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
};

/**
 * Get refresh token from storage
 * @returns {string|null} Refresh token or null
 */
export const getRefreshToken = () => {
  return localStorage.getItem("refreshToken") || sessionStorage.getItem("refreshToken");
};

/**
 * Save user and tokens to storage based on Remember Me preference
 * @param {Object} user - User object to save
 * @param {string} accessToken - JWT access token
 * @param {string} refreshToken - JWT refresh token
 * @param {boolean} rememberMe - Whether to persist login
 */
export const saveUser = (user, accessToken, refreshToken, rememberMe = false) => {
  const userData = JSON.stringify(user);

  if (rememberMe) {
    localStorage.setItem("user", userData);
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("rememberMe", "true");
  } else {
    sessionStorage.setItem("user", userData);
    sessionStorage.setItem("accessToken", accessToken);
    sessionStorage.setItem("refreshToken", refreshToken);
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("rememberMe");
  }

  try {
    if (user && user.is_secondary) {
      sessionStorage.setItem("special_mode", "1");
    } else {
      sessionStorage.removeItem("special_mode");
    }
  } catch (e) {
    // Silent error
  }
};

/**
 * Update access token in storage
 * @param {string} accessToken - New access token
 */
export const updateAccessToken = (accessToken) => {
  const hasRememberMe = localStorage.getItem("rememberMe") === "true";
  
  if (hasRememberMe) {
    localStorage.setItem("accessToken", accessToken);
  } else {
    sessionStorage.setItem("accessToken", accessToken);
  }
};

/**
 * Clear all user session data
 */
export const clearUserSession = () => {
  try {
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("rememberMe");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("special_mode");
  } catch (e) {
    // Silent error
  }
};

/**
 * Check if user is logged in
 * @returns {boolean}
 */
export const isLoggedIn = () => {
  return Boolean(getAccessToken() && getCurrentUser());
};

export const isSecondaryUser = () => {
  const user = getCurrentUser();
  return Boolean(user && user.is_secondary);
};

export const getDefaultHomePath = () => {
  return isSecondaryUser() ? "/special" : "/dashboard";
};
